/**
 * @fileoverview The four time-of-day blocks (WEB-658 / DPL-08) and their
 * boundary semantics. WEB-1228 (ruling R7) deleted the time-of-day filter
 * from Discover; what remains here is the boundary rule the event row uses
 * for its late-night cue (`DiscoverEventCard`) and the evening anchor in
 * `rowRangeLine.ts`.
 *
 * Every block is a **half-open interval** `[startHour, endHour)`:
 *
 * - `am`   → `[5, 12)`  (5:00am–11:59am)
 * - `pm`   → `[12, 17)` (12:00pm–4:59pm)
 * - `eve`  → `[17, 21)` (5:00pm–8:59pm)  ← 5:00pm resolves to `eve`, not `pm`
 * - `late` → `[21, 24) ∪ [0, 5)` (9:00pm–4:59am, wraps midnight) ← 9:00pm
 *   resolves to `late`, not `eve`
 *
 * This mirrors the backend's own bucket definition —
 * `TIME_OF_DAY_RANGES` / `hourMatchesTimeOfDay` in
 * `services/backend-api/src/services/discoverFacets.ts`.
 */

export const TIME_OF_DAY_SLUGS = ['am', 'pm', 'eve', 'late'] as const;
export type TimeOfDaySlug = (typeof TIME_OF_DAY_SLUGS)[number];

/**
 * Backend-mirrored hour boundaries (`TIME_OF_DAY_RANGES` in
 * `services/backend-api/src/services/discoverFacets.ts`), expressed against
 * the block slugs.
 */
export const TIME_OF_DAY_HOUR_RANGES: Record<
  TimeOfDaySlug,
  { readonly startHour: number; readonly endHour: number }
> = {
  am: { startHour: 5, endHour: 12 },
  pm: { startHour: 12, endHour: 17 },
  eve: { startHour: 17, endHour: 21 },
  // Wraps midnight: hour >= 21 OR hour < 5.
  late: { startHour: 21, endHour: 5 },
};

/**
 * Classify an already-resolved local hour (0–23, e.g. `date.getHours()` on a
 * `Date` already expressed in the target wall-clock time) into exactly one
 * of the four blocks, using the same half-open-interval boundary rule the
 * backend enforces. Does NOT resolve a timezone — callers must already have
 * a local hour.
 */
export function classifyLocalHourToTimeOfDaySlug(hour: number): TimeOfDaySlug {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    throw new RangeError(
      `classifyLocalHourToTimeOfDaySlug: hour must be an integer in [0, 23], got ${hour}`,
    );
  }
  // `late` first: it's the only wrapping range, so checking the two
  // non-wrapping bounds (`hour >= 21` / `hour < 5`) up front means every
  // other branch can assume a normal `[start, end)` interval.
  if (
    hour >= TIME_OF_DAY_HOUR_RANGES.late.startHour ||
    hour < TIME_OF_DAY_HOUR_RANGES.late.endHour
  ) {
    return 'late';
  }
  if (hour < TIME_OF_DAY_HOUR_RANGES.pm.startHour) {
    return 'am';
  }
  if (hour < TIME_OF_DAY_HOUR_RANGES.eve.startHour) {
    return 'pm';
  }
  return 'eve';
}
